import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Group,
  Button,
  Loader,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { Plus, User, Download } from 'react-feather';
import { coachesApi } from '../../services/api/coaches';
import type { Coach, CoachStatus } from '../../types';
import { AddCoachModal } from '../../components/coaches/AddCoachModal';
import { EmptyState, ExportModal } from '../../components/common';
import { useColumnConfig, useFetchList, type ColumnConfig } from '../../hooks';
import { CoachesFilters, CoachesListView, CoachesGridView } from './components';

type ViewMode = 'grid' | 'list';
type StatusFilter = CoachStatus | 'all';

// Default column configuration for coaches table
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'name', label: 'Coach', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
  { id: 'location', label: 'Location', visible: true, sortable: true },
  { id: 'timezone', label: 'Timezone', visible: true, sortable: true },
  { id: 'facilitator', label: 'Facilitator', visible: true, sortable: false },
  { id: 'certifications', label: 'Certifications', visible: true, sortable: false },
  { id: 'satisfaction', label: 'Satisfaction', visible: true, sortable: true },
  { id: 'cohorts', label: 'Cohorts', visible: true, sortable: true },
];

const SORT_BY_MAP: Record<string, string> = {
  name: 'name',
  location: 'location',
  timezone: 'timezone',
  cohorts: 'cohorts',
  satisfaction: 'satisfaction',
  status: 'status',
};

export function CoachesPage() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);
  const [sendingInvitation, setSendingInvitation] = useState<string | null>(null);

  // Use the reusable column config hook
  const {
    columns,
    setColumns,
    sortConfig,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    visibleColumnCount,
  } = useColumnConfig({
    preferenceKey: 'coaches_list_settings',
    defaultColumns: DEFAULT_COLUMNS,
    defaultPageSize: 25,
  });

  const fetchCoaches = useCallback(() => {
    const effectiveSortBy = sortConfig?.key || sortBy;
    const apiSortBy = SORT_BY_MAP[effectiveSortBy] || 'name';
    const apiSortOrder = sortConfig?.order || 'ASC';
    const statusFilter = selectedStatusFilter === 'all'
      ? undefined
      : [selectedStatusFilter] as CoachStatus[];

    return coachesApi.list({
      search: debouncedSearch || undefined,
      status: statusFilter,
      coach_type: undefined,
      sortBy: apiSortBy,
      sortOrder: apiSortOrder,
      page,
      limit: pageSize,
    });
  }, [debouncedSearch, selectedStatusFilter, sortBy, sortConfig, page, pageSize]);

  const { data: coaches, loading, totalCount, refetch } = useFetchList(fetchCoaches);
  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle send invitation
  const handleSendInvitation = async (coach: Coach, e: React.MouseEvent) => {
    e.stopPropagation();
    if ((!coach.email && !coach.preferred_email) || sendingInvitation) return;

    try {
      setSendingInvitation(coach.id);
      const result = await coachesApi.sendInvitation(coach.id);

      const recipients = [coach.email, coach.preferred_email].filter(Boolean).join(' and ');
      notifications.show({
        title: result.isResend ? 'Invitation Resent' : 'Invitation Sent',
        message: `SGOS invitation email ${result.isResend ? 'resent' : 'sent'} to ${recipients}`,
        color: 'green',
      });

      refetch();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send invitation',
        color: 'red',
      });
    } finally {
      setSendingInvitation(null);
    }
  };

  // Handle coach created
  const handleCoachCreated = (newCoach: Coach) => {
    closeAddModal();
    navigate(`/coaches/${newCoach.id}`);
  };

  // Handle row click
  const handleCoachClick = (coach: Coach) => {
    navigate(`/coaches/${coach.id}`);
  };

  // Handle status filter change with page reset
  const handleStatusFilterChange = (value: string) => {
    setSelectedStatusFilter(value as StatusFilter);
    setPage(1);
  };


  // Empty state (no coaches at all)
  const hasFilters = debouncedSearch || selectedStatusFilter !== 'all';
  if (!loading && coaches.length === 0 && !hasFilters) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          Coaches
        </Title>
        <EmptyState
          icon={<User size={40} strokeWidth={1.5} />}
          title="No coaches yet"
          description="Get started by adding your first coach to the system."
          action={
            <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
              Add Coach
            </Button>
          }
          size="lg"
          withCard
        />

        <AddCoachModal opened={addModalOpened} onClose={closeAddModal} onSuccess={handleCoachCreated} />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Title order={1}>Coaches</Title>
        <Group gap="sm">
          <Button variant="default" leftSection={<Download size={18} />} onClick={openExportModal}>
            Export
          </Button>
          <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
            Add Coach
          </Button>
        </Group>
      </Group>

      {/* Search and Filter Panel */}
      <CoachesFilters
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        selectedStatusFilter={selectedStatusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Content */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : viewMode === 'list' ? (
        <CoachesListView
          coaches={coaches}
          columns={columns}
          onColumnsChange={setColumns}
          sortConfig={sortConfig}
          onSort={handleSort}
          visibleColumnCount={visibleColumnCount}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onCoachClick={handleCoachClick}
          onSendInvitation={handleSendInvitation}
          sendingInvitation={sendingInvitation}
        />
      ) : (
        <CoachesGridView
          coaches={coaches}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onCoachClick={handleCoachClick}
        />
      )}

      {/* Add Coach Modal */}
      <AddCoachModal opened={addModalOpened} onClose={closeAddModal} onSuccess={handleCoachCreated} />

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="coaches"
        recordCount={totalCount}
        filterContext={{
          search: debouncedSearch || undefined,
          status: selectedStatusFilter === 'all' ? undefined : [selectedStatusFilter],
        }}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        filterSummary={[
          ...(debouncedSearch ? [{ label: 'Search', value: debouncedSearch }] : []),
          ...(selectedStatusFilter !== 'all' ? [{ label: 'Status', value: selectedStatusFilter }] : []),
        ]}
      />
    </Container>
  );
}

export default CoachesPage;
